import { isDesktop, isMobileOnly, isTablet } from 'react-device-detect';
import {
  SEARCH_PARAM_CONFIRMATION_MODAL,
  SEARCH_PARAM_STATUS,
} from '~/app/[locale]/home/my-orders/offers-pending/[id]/constants';
import { ApiOfferStatusEnum, TermsAndConditionsEnum } from '~/types-and-enums/offerTypes';

export const DEVICE_TYPE = {
  MOBILE: 'Mobile',
  TABLET: 'Tablet',
  DESKTOP: 'Desktop',
  UNKNOWN: 'Unknown',
};

type DeviceTypeProps = {
  isMobile: boolean;
  isNotMobile?: boolean;
  isDesktop?: boolean;
  isTablet?: boolean;
};

/**
 * Get the device type based on react-device-detect library
 * @returns DeviceTypeProps
 */
export const getDeviceTypeClientSide = (): DeviceTypeProps => {
  if (typeof navigator === 'undefined') {
    return {
      isMobile: false,
      isNotMobile: false,
      isDesktop: true, // Default to desktop if userAgent is not available
      isTablet: false,
    };
  }

  return {
    isMobile: isMobileOnly,
    isNotMobile: !isMobileOnly,
    isDesktop: isDesktop,
    isTablet: isTablet,
  };
};

/**
 * Updates the URL search params with the modal=true status=ApiOfferStatusEnum
 * So that the ConfirmationModal listens to the event and opens the confirmation modal
 * Update the browser's history stack without rerendering the page.
 * @param status
 */
export const openConfirmationModal = (
  status?: ApiOfferStatusEnum | TermsAndConditionsEnum,
  extraParams?: URLSearchParams,
) => {
  const params = new URLSearchParams(extraParams?.toString());
  params.set(SEARCH_PARAM_CONFIRMATION_MODAL, 'true');
  if (status) params.set(SEARCH_PARAM_STATUS, status);
  updateSearchParams(params);
};

/**
 * Client side update only
 * Removes a specific search parameter from the URL search params
 * Update the browser's history stack without rerendering the page.
 * @param paramKey - The key of the search param to remove
 * @param currentSearchParams - Optional current search params
 */
export const removeSearchParam = (paramKey: string, currentSearchParams?: URLSearchParams) => {
  // Create a new URLSearchParams instance to make modifications
  const searchParams = currentSearchParams
    ? new URLSearchParams(currentSearchParams.toString())
    : new URLSearchParams(window.location.search);

  // Remove the specified search parameter
  searchParams.delete(paramKey);

  // Update the URL without reloading the page
  window.history.replaceState(null, '', `?${searchParams.toString()}`);
};

/**
 * Client side update only
 * Updates the URL search params with the params provided
 * Update the browser's history stack without rerendering the page.
 * @param params
 */
export const updateSearchParams = (params: URLSearchParams | string) => {
  window.history.replaceState(null, '', `?${params.toString()}`);
};

/**
 * Updates URL search parameters with new parameters, with optional toggle behavior
 * Client side update only - updates the browser's history stack without rerendering the page
 * @param newParam - New parameter(s) to add or toggle in the URL search params
 * @param currentSearchParams - Optional current search parameters to start from
 * @param toggle - Whether to toggle existing parameters (default: true)
 */
export const updateSearchParamsNew = (
  newParam: URLSearchParams | string,
  currentSearchParams?: URLSearchParams,
  toggle: boolean = true,
) => {
  const params = new URLSearchParams(currentSearchParams);
  /**
   * Converts the input parameter to an array of key-value pairs.
   * If the input is a string, it splits the string into a key-value pair.
   * If the input is a URLSearchParams object, it converts the object into an array of key-value pairs.
   */
  const entries =
    typeof newParam === 'string'
      ? [newParam.split('=') as [string, string]]
      : Array.from(newParam.entries());

  // Iterate through the entries and update the URL search params
  // If the parameter already exists and toggle is true, delete it
  // Otherwise, set the parameter
  for (const [key, value] of entries) {
    if (params.has(key) && toggle) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }
  // Update the URL without reloading the page
  window.history.replaceState(null, '', `?${params.toString()}`);
};

/**
 * Client side update only
 * - Redirects to the provided pathname with the params provided
 * @param pathname - Pathname to redirect to
 * @param params - URLSearchParams or string
 */
export const redirectToPage = (pathname: string, params?: URLSearchParams | string) => {
  const paramsString = params ? `?${params?.toString()}` : '';
  window.history.replaceState(null, '', `${pathname}${paramsString}`);
};

/**
 * Gets item from local storage based on provided key
 * @param localStorageKey
 * @returns
 */
export const getItemFromLocalStorage = (localStorageKey: string) => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(localStorageKey);
  }
};

/**
 * Sets item to local storage based on provided key and value
 * @param localStorageKey
 * @param value
 * @returns
 */
export const setItemToLocalStorage = (localStorageKey: string, value: string) => {
  if (typeof window !== 'undefined') {
    return localStorage.setItem(localStorageKey, value);
  }
};

/**
 * Function to update local storage and trigger custom event
 */
export const updateHasSeenModal = (value: string) => {
  setItemToLocalStorage('hasSeenModal', value);
  const event = new Event('customLocalStorageEvent');
  window.dispatchEvent(event);
};

export const isBrowserClientSide = () => typeof window !== 'undefined';

/**
 * Checks if the current device is a touch device.
 *
 * @return {boolean} True if the device is a touch device, false otherwise.
 */
export const isTouchDevice = () => {
  if (isBrowserClientSide()) {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
  return false;
};

export const isClient = () => typeof window !== 'undefined';

/**
 * Scrolls to the bottom of the screen or the specified element based on ID
 * @param elementId - The ID of the element to scroll to
 * @returns - Void
 */
export const scrollToBottom = (elementId?: string) => {
  if (typeof window === 'undefined') return; // Ensure it runs only in the browser

  if (elementId) {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }
};

/**
 * Scrolls to the top of the screen or the specified element based on ID
 * @param elementId - The ID of the element to scroll to
 * @returns - Void
 */
export const scrollToTop = (elementId?: string) => {
  if (typeof window === 'undefined') return; // Ensure it runs only in the browser

  if (elementId) {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
